import { lazy, Suspense, useCallback, useState } from 'react'

import DotsHorizontalIcon from '@heroicons/react/outline/esm/DotsHorizontalIcon.js'
import PencilAltIcon from '@heroicons/react/outline/esm/PencilAltIcon.js'
import PlusIcon from '@heroicons/react/outline/esm/PlusIcon.js'
import TrashIcon from '@heroicons/react/outline/esm/TrashIcon.js'
import ViewListIcon from '@heroicons/react/outline/esm/ViewListIcon.js'

import { hasScopesPermission } from '@tribeplatform/gql-client/lib'
import { Collection } from '@tribeplatform/gql-client/types'
import { T } from '@tribeplatform/react-components/i18n'
import { useNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Dropdown } from '@tribeplatform/react-ui-kit/Dropdown'

const CollectionDeleteModal = lazy(() =>
  import('@tribeplatform/react-components/Collection').then(m => ({
    default: m.CollectionDeleteModal,
  })),
)
const CollectionFormModal = lazy(() =>
  import('@tribeplatform/react-components/Collection').then(m => ({
    default: m.CollectionFormModal,
  })),
)
const CollectionOrganizationModal = lazy(() =>
  import('@tribeplatform/react-components/Collection').then(m => ({
    default: m.CollectionOrganizationModal,
  })),
)
const CreateSpaceModal = lazy(() =>
  import('@tribeplatform/react-components/Space').then(m => ({
    default: m.CreateSpaceFromTemplateModal,
  })),
)

export const CollectionHeaderActionButtons = ({
  collection,
}: {
  collection?: Collection
}) => {
  const [openOrganizationModal, setOpenOrganizationModal] = useState(false)
  const [openSpaceModal, setOpenSpaceModal] = useState(false)
  const [openCollectionModal, setOpenCollectionModal] = useState(false)
  const [openDeleteCollectionModal, setOpenDeleteCollectionModal] =
    useState(false)
  const { data: network = {} } = useNetwork()

  const [canCreateSpace, canCreateCollection] = hasScopesPermission(network, [
    'addSpace',
    'createCollection',
  ])

  const onOrganizationModalClose = useCallback(() => {
    setOpenOrganizationModal(false)
  }, [])

  const onOrganizeClick = useCallback(() => {
    setOpenOrganizationModal(true)
  }, [])

  const onDeleteClick = useCallback(() => {
    setOpenDeleteCollectionModal(true)
  }, [])

  const closeCollectionModal = useCallback(() => {
    setOpenCollectionModal(false)
  }, [])

  const deleteCollectionModal = useCallback(() => {
    setOpenDeleteCollectionModal(false)
  }, [])

  const closeCreateSpaceModal = useCallback(() => {
    setOpenSpaceModal(false)
  }, [])

  const openCreateSpaceModal = useCallback(() => {
    setOpenSpaceModal(true)
  }, [])

  const openEditCollectionModal = useCallback(() => {
    setOpenCollectionModal(true)
  }, [])

  return (
    <>
      {/* Actions Dropdown */}
      {canCreateCollection && (
        <Dropdown placement="bottom-end">
          <Dropdown.ButtonMinimal as="div">
            <Button variant="outline" leadingIcon={<DotsHorizontalIcon />} />
          </Dropdown.ButtonMinimal>
          <Dropdown.Items className="-mt-2">
            <Dropdown.Item
              leadingIcon={<PencilAltIcon />}
              onClick={openEditCollectionModal}
            >
              <T
                id="Blocks.CollectionHeader.EditCollection"
                defaultMessage="Edit {COLLECTION}"
              />
            </Dropdown.Item>
            <Dropdown.Item
              leadingIcon={<ViewListIcon />}
              onClick={onOrganizeClick}
            >
              <T id="Generics.Organize" defaultMessage="Organize" />
            </Dropdown.Item>
            <Dropdown.Item leadingIcon={<TrashIcon />} onClick={onDeleteClick}>
              <T
                id="Blocks.CollectionHeader.DeleteCollection"
                defaultMessage="Delete {COLLECTION}"
              />
            </Dropdown.Item>
          </Dropdown.Items>
        </Dropdown>
      )}
      {openCollectionModal && (
        <Suspense fallback={<></>}>
          <CollectionFormModal
            open={openCollectionModal}
            collection={collection}
            onClose={closeCollectionModal}
          />
        </Suspense>
      )}
      {openDeleteCollectionModal && (
        <Suspense fallback={<></>}>
          <CollectionDeleteModal
            open={openDeleteCollectionModal}
            onClose={deleteCollectionModal}
            collection={collection}
          />
        </Suspense>
      )}
      {/* Create Space Button */}
      {canCreateSpace && (
        <Button leadingIcon={<PlusIcon />} onClick={openCreateSpaceModal}>
          <T id="Generics.AddSpace" defaultMessage="Add {SPACE}" />
        </Button>
      )}
      {openSpaceModal ? (
        <Suspense fallback={<></>}>
          <CreateSpaceModal
            open={openSpaceModal}
            space={{
              collectionId: collection?.id,
            }}
            onClose={closeCreateSpaceModal}
          />
        </Suspense>
      ) : null}
      {openOrganizationModal && (
        <Suspense fallback={<></>}>
          <CollectionOrganizationModal
            open={openOrganizationModal}
            onClose={onOrganizationModalClose}
            collection={collection}
          />
        </Suspense>
      )}
    </>
  )
}
